import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { connectMetaApiAccount } from "wasp/client/operations";
import useTranslation from "../client/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../client/components/ui/dialog";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { useToast } from "../client/hooks/use-toast";

const schema = z.object({
  login: z.string().min(1),
  password: z.string().min(1),
  server: z.string().min(1),
  platform: z.enum(["mt4", "mt5"]),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  accountId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function MetaApiConnectModal({ open, accountId, onClose, onSuccess }: Props) {
  const t = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { platform: "mt5" } });

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    try {
      await connectMetaApiAccount({ accountId, ...data });
      toast({ title: t.metaApi.connect });
      onSuccess();
      onClose();
    } catch (err: any) {
      toast({
        title: t.metaApi.errors.connectionFailed,
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.metaApi.connectModal.title}</DialogTitle>
          <DialogDescription>{t.metaApi.connectModal.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-2">
          <div className="space-y-1">
            <Label>{t.metaApi.connectModal.loginLabel}</Label>
            <Input {...register("login")} placeholder={t.metaApi.connectModal.loginPlaceholder} />
            {errors.login && <p className="text-xs text-destructive">{errors.login.message}</p>}
          </div>
          <div className="space-y-1">
            <Label>{t.metaApi.connectModal.passwordLabel}</Label>
            <Input type="password" {...register("password")} />
            {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
          </div>
          <div className="space-y-1">
            <Label>{t.metaApi.connectModal.serverLabel}</Label>
            <Input {...register("server")} placeholder={t.metaApi.connectModal.serverPlaceholder} />
            {errors.server && <p className="text-xs text-destructive">{errors.server.message}</p>}
          </div>
          <div className="space-y-1">
            <Label>{t.metaApi.connectModal.platformLabel}</Label>
            <Select defaultValue="mt5" onValueChange={(v) => setValue("platform", v as "mt4" | "mt5")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mt5">MetaTrader 5</SelectItem>
                <SelectItem value="mt4">MetaTrader 4</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t.metaApi.connectModal.cancel}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t.metaApi.connectModal.connecting : t.metaApi.connectModal.submitButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
